import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { Button, ButtonVariants, PrimeTable, rem } from '@triumph/design-system';
import { isAxiosError } from 'axios';
import { orderBy } from 'es-toolkit';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useOpenS3File } from '@/components/hooks/useOpenS3File';
import { useToast } from '@/components/hooks/useToast';
import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import { usePermissions } from '@/components/providers/PermissionsContextProvider';
import { useSyncDocumentsFromAlloyMutation } from '@/components/shared/api/mutations/applicant';
import {
    FilenameRejectedError,
    useUpdateFilenameMutation,
    useUpdateDocumentTypeMutation,
    useSoftDeleteFileMutation,
} from '@/components/shared/api/mutations/files';
import {
    useApplicantDocumentsQuery,
    useApplicantMissingDocumentsQuery,
} from '@/components/shared/api/queries/applicant';
import { StripedDataTableWrapper } from '@/components/shared/components/common.StyledComponents';
import { FileUploadDialog } from '@/components/shared/components/FileUploadDialog';
import { TabDataState } from '@/components/shared/components/TabDataState';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';
import { Feature, PERMISSION_DENIED_TITLE } from '@/components/shared/constants/features';
import {
    EVENT_LOG_NAMES,
    getLoggingContext,
    honeybadgerLogEvent,
} from '@/components/shared/utils/logging';
import { convertHeicToPng, formatDateWithTimezone } from '@/components/shared/utils/utils';
import { useCurrentUserEmail } from '@/components/hooks/useCurrentUserEmail';
import { useOktaAuth } from '@okta/okta-react';

import { DDDCard, DDDCardHeader, DDDCardTitle } from '../../Applicant.StyledComponents';
import { ApplicantTabId, Document, FileType } from '../../Applicant.types';
import { Au10tixDddLink } from '../Au10tixDddLink';
import { EditableDocumentTypeCell } from './EditableDocumentTypeCell';
import { EditableFilenameCell } from './EditableFilenameCell';
import { useApplicantPresignedUpload } from './useApplicantPresignedUpload';

const FileUploadWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: ${rem(8)};
`;

// The table has many columns, so the card spans the tab instead of DDD's 1100px cap.
const DocumentsCard = styled(DDDCard)`
    max-width: none;
`;

const DocumentsCardHeader = styled(DDDCardHeader)`
    align-items: center;
    justify-content: space-between;
`;

const DocumentTypeCellWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: ${rem(12)};
`;

const DocumentsCardBody = styled.div`
    padding: ${rem(16)} ${rem(20)};
`;

const MissingDocumentsWarning = styled.div`
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: ${rem(4)};
    color: #856404;
    padding: ${rem(12)} ${rem(16)};
    margin-bottom: ${rem(16)};
    font-size: ${rem(14)};
`;

const NoMissingDocumentsMessage = styled.div`
    background-color: #d1e7dd;
    border: 1px solid #0f5132;
    border-radius: ${rem(4)};
    color: #0f5132;
    padding: ${rem(12)} ${rem(16)};
    margin-bottom: ${rem(16)};
    font-size: ${rem(14)};
`;

const NoAlloyJourneyMessage = styled.div`
    background-color: #e2e8f0;
    border: 1px solid #94a3b8;
    border-radius: ${rem(4)};
    color: #475569;
    padding: ${rem(12)} ${rem(16)};
    margin-bottom: ${rem(16)};
    font-size: ${rem(14)};
`;

const TableWrapper = styled(StripedDataTableWrapper)`
    .p-datatable .p-datatable-table {
        table-layout: fixed;
    }

    .p-datatable .p-datatable-tbody > tr > td {
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const ConditionLinkButton = styled.button`
    display: block;
    max-width: 100%;
    background: none;
    border: none;
    padding: 0;
    color: #0000ee;
    text-decoration: underline;
    cursor: pointer;
    font: inherit;
    text-align: left;

    &:hover {
        color: #0000cc;
    }
    &:focus-visible {
        outline: 2px solid #0000ee;
        outline-offset: 2px;
        border-radius: 2px;
    }
`;

const ConditionLinkText = styled.span`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    overflow-wrap: anywhere;
`;

const PermissionDeniedMessage = styled.div`
    padding: ${rem(24)} 0;
    font-size: ${rem(14)};
    color: #6b7280;
    font-style: italic;
`;

const DeletedSectionToggle = styled.button`
    background: none;
    border: none;
    padding: ${rem(8)} 0;
    color: #6b7280;
    font-size: ${rem(14)};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: ${rem(6)};
    margin-top: ${rem(16)};

    &:hover {
        color: #374151;
    }
`;

type DocumentActionType = 'upload' | 'rename' | 'documentType' | 'sync' | 'delete';

const SUCCESS_TOAST_MESSAGE_BY_ACTION_TYPE: Record<DocumentActionType, string> = {
    upload: 'Document uploaded successfully',
    rename: 'Filename updated successfully',
    documentType: 'Document type updated successfully',
    sync: 'Documents synced successfully from Alloy',
    delete: 'Document deleted successfully',
};

const FAIL_TOAST_MESSAGE_BY_ACTION_TYPE: Record<DocumentActionType, string> = {
    upload: 'Upload failed. Please try again',
    rename: 'Failed to update filename',
    documentType: 'Failed to update document type',
    sync: 'Failed to sync documents from Alloy',
    delete: 'Failed to delete document',
};

const RENAME_DISALLOWED_CHARACTERS_MESSAGE =
    'That file name contains characters that aren\u2019t allowed.';

// The API's own wording for a field that failed its character or format checks.
const FIELD_VALIDATION_MESSAGE = 'Invalid format';

// A refused rename is a 400 whose errors.FileName gives the reason (a disallowed type or characters).
const getRenameFailureMessage = (error: Error): string | null => {
    if (error instanceof FilenameRejectedError && error.message) return error.message;
    if (!isAxiosError(error) || error.response?.status !== 400) return null;
    const data = error.response.data as { errors?: { FileName?: string[] } } | undefined;
    const reason = data?.errors?.FileName?.[0];
    return reason && reason !== FIELD_VALIDATION_MESSAGE
        ? reason
        : RENAME_DISALLOWED_CHARACTERS_MESSAGE;
};

const DOCUMENTS_LOAD_ERROR_MESSAGE = 'Could not load documents.';

const DOCUMENTS_EMPTY_MESSAGE = 'No results found';

const MISSING_DOCUMENTS_LOAD_ERROR_MESSAGE = 'Could not load Alloy document status.';

const MISSING_DOCUMENTS_TEST_ID = 'missing-documents';

export const ApplicantDocument = ({ onDownloadReport }: { onDownloadReport?: () => void } = {}) => {
    const { applicantId } = useApplicantContext();
    const { hasFeature } = usePermissions();
    const queryClient = useQueryClient();
    const { authState } = useOktaAuth();
    const currentUserEmail = useCurrentUserEmail();
    const [searchParams, setSearchParams] = useSearchParams();

    const goToCondition = (conditionId: string) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', ApplicantTabId.Conditions);
        next.set('conditionId', conditionId);
        setSearchParams(next);
    };

    const [showDeletedDocuments, setShowDeletedDocuments] = useState(false);
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const { isRenameResultEnabled } = useFeatureFlags();
    const [pendingDeleteFileId, setPendingDeleteFileId] = useState<string | null>(null);

    const { toast, showToast } = useToast('applicant-document-toast');
    const showActionSuccess = (actionType: DocumentActionType) =>
        showToast('success', SUCCESS_TOAST_MESSAGE_BY_ACTION_TYPE[actionType]);
    const showActionFailure = (actionType: DocumentActionType) =>
        showToast('error', FAIL_TOAST_MESSAGE_BY_ACTION_TYPE[actionType]);

    const { handleFileClick, getFileDragProps } = useOpenS3File();
    const { uploadApplicantDocAws } = useApplicantPresignedUpload();
    const { mutate: updateFilename } = useUpdateFilenameMutation(applicantId ?? '', {
        onSuccess: () => {
            showActionSuccess('rename');
            // Invalidate applicant query to refresh main applicant data
            queryClient.invalidateQueries({
                queryKey: ['applicant', applicantId],
            });
        },
        onError: (error) => {
            showToast(
                'error',
                (isRenameResultEnabled && getRenameFailureMessage(error)) ||
                    FAIL_TOAST_MESSAGE_BY_ACTION_TYPE.rename,
            );
        },
    });

    const { mutate: updateDocumentType } = useUpdateDocumentTypeMutation(applicantId ?? '', {
        onSuccess: (updated) => {
            if (updated === false) {
                showActionFailure('documentType');
                return;
            }

            showActionSuccess('documentType');
            queryClient.invalidateQueries({
                queryKey: ['applicant', applicantId],
            });
        },
        onError: () => {
            showActionFailure('documentType');
        },
    });

    const { mutate: softDeleteFile, isPending: isDeletingFile } = useSoftDeleteFileMutation(
        applicantId ?? '',
        {
            onSuccess: () => {
                showActionSuccess('delete');
            },
            onError: () => {
                showActionFailure('delete');
            },
        },
    );

    const { mutate: syncDocumentsFromAlloy, isPending: isSyncingDocuments } =
        useSyncDocumentsFromAlloyMutation();

    const handleSyncDocuments = () => {
        if (!applicantId) return;

        syncDocumentsFromAlloy(
            { applicantId },
            {
                onSuccess: () => {
                    showActionSuccess('sync');
                    // Refetch documents to show newly synced documents
                    queryClient.invalidateQueries({
                        queryKey: ['applicant-documents', applicantId],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ['applicant-missing-documents', applicantId],
                    });
                },
                onError: () => {
                    showActionFailure('sync');
                },
            },
        );
    };

    const canListDocuments = hasFeature(Feature.ViewDocumentList);

    const {
        data: documentsData,
        isLoading: isLoadingDocuments,
        isLoadingError: isDocumentsLoadingError,
        refetch: refetchDocuments,
    } = useApplicantDocumentsQuery({
        applicantId: applicantId ?? '',
        enabled: canListDocuments,
    });

    const {
        data: missingDocumentsData,
        isLoading: isLoadingMissingDocuments,
        isLoadingError: isMissingDocumentsLoadingError,
        refetch: refetchMissingDocuments,
    } = useApplicantMissingDocumentsQuery({
        applicantId: applicantId ?? '',
    });

    const { documents, deletedDocuments } = useMemo(() => {
        const sorted = orderBy(documentsData ?? [], ['uploadDateUtc'], ['desc']);
        const active = sorted.filter((file) => !file.isDeleted);
        const deleted = sorted.filter((file) => file.isDeleted);
        return { documents: active, deletedDocuments: deleted };
    }, [documentsData]);

    const handleOpenUploadDialog = () => {
        setShowUploadDialog(true);
    };

    const handleUploadFile = async (file: File, fileType: FileType | null) => {
        if (!applicantId) return;

        const userId = authState?.idToken?.claims?.sub;

        honeybadgerLogEvent(EVENT_LOG_NAMES.AddNewDocumentClick, {
            level: 'INFO',
            customAttributes: {
                ...getLoggingContext(currentUserEmail, userId),
                currentPage: window.location.pathname,
                timestamp: new Date().toISOString(),
                applicantId,
            },
        });

        const convertedFile = await convertHeicToPng(file);
        const doc = await uploadApplicantDocAws(
            applicantId,
            convertedFile,
            fileType ?? FileType.Other,
        );

        if (!doc) {
            throw new Error('Upload failed.');
        }
    };

    const handleUploadBatchComplete = ({ succeeded }: { succeeded: File[]; failed: File[] }) => {
        if (!applicantId || succeeded.length === 0) return;
        queryClient.invalidateQueries({ queryKey: ['applicant-documents', applicantId] });
        // The missing-documents panel counts the ORP's files, so a new upload changes it too.
        queryClient.invalidateQueries({ queryKey: ['applicant-missing-documents', applicantId] });
        showActionSuccess('upload');
    };

    const handleDeleteFile = (fileId: string) => {
        setPendingDeleteFileId(fileId);
    };

    const handleConfirmDelete = () => {
        if (!pendingDeleteFileId) return;

        softDeleteFile(pendingDeleteFileId);
        setPendingDeleteFileId(null);
    };

    const handleCancelDelete = () => {
        setPendingDeleteFileId(null);
    };

    const handleSaveFilename = (fileId: string, fileName: string) => {
        if (!fileName.trim()) {
            return;
        }

        updateFilename({ fileId, fileName });
    };

    const handleSaveDocumentType = (fileId: string, documentType: FileType) => {
        updateDocumentType({ fileId, documentType });
    };

    const columns = useMemo(
        () => [
            {
                body: ({ uploadDateUtc }: Document) => (
                    <div>{formatDateWithTimezone(uploadDateUtc)}</div>
                ),
                field: 'uploadDateUtc',
                header: 'Date',
                sortable: false,
                style: { width: rem(140) },
            },
            {
                body: (file: Document) => {
                    const fileType = FileType[file.documentType as keyof typeof FileType];
                    const isClearDocument = fileType === FileType.Clear;
                    const hasOpenPermission =
                        hasFeature(Feature.OpenDocument) &&
                        (!isClearDocument || hasFeature(Feature.ViewClearReport));

                    return (
                        <EditableFilenameCell
                            file={file}
                            onFileClick={handleFileClick}
                            dragProps={
                                hasOpenPermission
                                    ? getFileDragProps(file.fileId, file.fileName)
                                    : undefined
                            }
                            onSaveFilename={handleSaveFilename}
                            hasViewPermission={hasOpenPermission}
                            hasEditPermission={hasFeature(Feature.AddDocuments)}
                        />
                    );
                },
                field: 'fileName',
                header: 'File Name',
                sortable: false,
                style: { maxWidth: 0, width: '40%' },
            },
            {
                body: (file: Document) => (
                    <DocumentTypeCellWrapper>
                        <EditableDocumentTypeCell
                            file={file}
                            onSaveDocumentType={handleSaveDocumentType}
                            hasEditPermission={hasFeature(Feature.AddDocuments)}
                        />
                        <Au10tixDddLink fileId={file.fileId} />
                    </DocumentTypeCellWrapper>
                ),
                field: 'documentType',
                header: 'Document Type',
                sortable: false,
                style: { width: rem(280) },
            },
            {
                body: ({ association, linkedConditionId }: Document) =>
                    linkedConditionId ? (
                        <span>
                            <strong>Associated Task/Condition:</strong>{' '}
                            <ConditionLinkButton
                                type="button"
                                onClick={() => goToCondition(linkedConditionId)}
                                data-testid={`document-condition-link-${linkedConditionId}`}
                                title={association}
                            >
                                <ConditionLinkText>{association}</ConditionLinkText>
                            </ConditionLinkButton>
                        </span>
                    ) : (
                        <span>{association}</span>
                    ),
                field: 'association',
                header: 'Association',
                sortable: false,
                style: { width: rem(220) },
            },
            {
                field: 'uploadedBy',
                header: 'Uploaded By',
                sortable: false,
                style: { width: rem(180) },
            },
            ...(hasFeature(Feature.AddDocuments)
                ? [
                      {
                          body: ({ fileId, isDeleted }: Document) =>
                              !isDeleted ? (
                                  <button
                                      type="button"
                                      aria-label="Delete document"
                                      data-testid={`delete-document-btn-${fileId}`}
                                      disabled={isDeletingFile}
                                      onClick={() => handleDeleteFile(fileId)}
                                      style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          color: '#6b7280',
                                          padding: 0,
                                      }}
                                  >
                                      🗑️
                                  </button>
                              ) : null,
                          field: 'delete',
                          header: '',
                          sortable: false,
                          style: { width: rem(48) },
                      },
                  ]
                : []),
        ],
        [
            handleFileClick,
            getFileDragProps,
            hasFeature,
            handleSaveFilename,
            handleSaveDocumentType,
            goToCondition,
            handleDeleteFile,
            isDeletingFile,
        ],
    );

    return (
        <div>
            {toast}
            <DocumentsCard>
                <DocumentsCardHeader>
                    <DDDCardTitle>Documents</DDDCardTitle>
                    <Button
                        variant={ButtonVariants.secondary}
                        onClick={handleSyncDocuments}
                        disabled={!applicantId || missingDocumentsData?.hasAlloyJourney !== true}
                        loading={isSyncingDocuments}
                        data-testid="sync-alloy-documents-button"
                    >
                        Sync Documents From Alloy
                    </Button>
                </DocumentsCardHeader>
                <DocumentsCardBody>
                    <TabDataState
                        isLoading={isLoadingMissingDocuments}
                        isLoadingError={isMissingDocumentsLoadingError}
                        onRetry={() => refetchMissingDocuments()}
                        errorMessage={MISSING_DOCUMENTS_LOAD_ERROR_MESSAGE}
                        testId={MISSING_DOCUMENTS_TEST_ID}
                    >
                        {missingDocumentsData?.hasAlloyJourney === false ? (
                            <NoAlloyJourneyMessage data-testid="no-alloy-journey-message">
                                This applicant does not have an Alloy journey. Document sync is not
                                available.
                            </NoAlloyJourneyMessage>
                        ) : null}
                        {missingDocumentsData?.hasAlloyJourney &&
                        missingDocumentsData.hasMissingDocuments ? (
                            <MissingDocumentsWarning data-testid="missing-documents-warning">
                                ⚠️ {missingDocumentsData.missingDocumentCount} document
                                {missingDocumentsData.missingDocumentCount !== 1 ? 's' : ''}{' '}
                                didn't download from Alloy. Click the "Sync Documents From Alloy"
                                button above to retry.
                            </MissingDocumentsWarning>
                        ) : null}
                        {missingDocumentsData?.hasAlloyJourney &&
                        !missingDocumentsData.hasMissingDocuments ? (
                            <NoMissingDocumentsMessage data-testid="no-missing-documents-message">
                                ✓ All documents have been successfully downloaded from Alloy.
                            </NoMissingDocumentsMessage>
                        ) : null}
                    </TabDataState>
                    {canListDocuments ? (
                        <TabDataState
                            isLoading={isLoadingDocuments}
                            isLoadingError={isDocumentsLoadingError}
                            onRetry={() => refetchDocuments()}
                            errorMessage={DOCUMENTS_LOAD_ERROR_MESSAGE}
                        >
                            <TableWrapper>
                                <PrimeTable
                                    columns={columns}
                                    emptyMessage={DOCUMENTS_EMPTY_MESSAGE}
                                    scrollable
                                    scrollHeight="400px"
                                    showGridlines={false}
                                    stripedRows
                                    value={documents}
                                />
                            </TableWrapper>
                            {deletedDocuments.length > 0 ? (
                                <>
                                    <DeletedSectionToggle
                                        onClick={() => setShowDeletedDocuments((prev) => !prev)}
                                        data-testid="toggle-deleted-documents"
                                    >
                                        {showDeletedDocuments ? '▾' : '▸'} Deleted files (
                                        {deletedDocuments.length})
                                    </DeletedSectionToggle>
                                    {showDeletedDocuments ? (
                                        <TableWrapper>
                                            <PrimeTable
                                                columns={columns}
                                                scrollable
                                                scrollHeight="400px"
                                                showGridlines={false}
                                                stripedRows
                                                tableStyle={{ opacity: 0.6 }}
                                                value={deletedDocuments}
                                                data-testid="deleted-documents-table"
                                            />
                                        </TableWrapper>
                                    ) : null}
                                </>
                            ) : null}
                        </TabDataState>
                    ) : (
                        <PermissionDeniedMessage data-testid="documents-permission-denied">
                            You don&apos;t have permission to view this applicant&apos;s documents.
                        </PermissionDeniedMessage>
                    )}
                    <FileUploadWrapper>
                        <div title={!canListDocuments ? PERMISSION_DENIED_TITLE : undefined}>
                            {onDownloadReport && (
                                <Button
                                    variant={ButtonVariants.secondary}
                                    onClick={onDownloadReport}
                                    disabled={!canListDocuments}
                                    data-testid="download-orp-report-button"
                                >
                                    Download ORP Report
                                </Button>
                            )}
                        </div>
                        <div
                            title={
                                !hasFeature(Feature.AddDocuments)
                                    ? PERMISSION_DENIED_TITLE
                                    : undefined
                            }
                        >
                            <Button
                                variant={ButtonVariants.primary}
                                onClick={handleOpenUploadDialog}
                                disabled={!hasFeature(Feature.AddDocuments)}
                                data-testid="add-new-document-button"
                            >
                                ⬆️ Add New Document
                            </Button>
                        </div>
                    </FileUploadWrapper>
                </DocumentsCardBody>
            </DocumentsCard>
            {pendingDeleteFileId !== null && (
                <div
                    data-testid="delete-document-confirm-dialog"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: rem(8),
                            padding: rem(24),
                            maxWidth: rem(400),
                            width: '100%',
                        }}
                    >
                        <p style={{ marginBottom: rem(16) }}>
                            Are you sure you want to delete this document? This action cannot be
                            undone.
                        </p>
                        <div style={{ display: 'flex', gap: rem(8), justifyContent: 'flex-end' }}>
                            <Button
                                variant={ButtonVariants.secondary}
                                onClick={handleCancelDelete}
                                data-testid="delete-document-cancel-btn"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant={ButtonVariants.primary}
                                onClick={handleConfirmDelete}
                                data-testid="delete-document-confirm-btn"
                            >
                                Delete
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <FileUploadDialog
                id="add-new-document-modal"
                header="Add New Document"
                visible={showUploadDialog}
                onClose={() => setShowUploadDialog(false)}
                onUploadFile={handleUploadFile}
                onBatchComplete={handleUploadBatchComplete}
                submitLabel="Upload"
                data-testid="add-new-document-dialog"
            />
        </div>
    );
};
